import React, { useState } from 'react';
import { 
  FiBook, 
  FiKey, 
  FiCopy, 
  FiCheck, 
  FiServer, 
  FiLock, 
  FiCode,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiExternalLink,
  FiMonitor,
  FiPlay,
  FiTrash2,
  FiLogIn,
  FiActivity,
  FiUser,
  FiUsers,
  FiSettings,
  FiShield,
  FiZap,
  FiArrowLeft
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoginAuthUser from '../../baseUrl/LoginAuthUser';
import './ApiDocs.css';

// Type definitions
interface NavItem {
  id: string;
  label: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path?: string;
  description?: string;
  adminOnly?: boolean;
}

interface NavGroup {
  id: string;
  title: string;
  icon: React.ElementType;
  items: NavItem[];
}

interface CodeBlockProps {
  language: string;
  code: string;
  title?: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ language, code, title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  return (
    <div className="code-block">
      {title && <div className="code-block-title">{title}</div>}
      <div className="code-block-header">
        <span className="code-language">{language}</span>
        <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
          {copied ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}
        </button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
};

// Navigation items configuration
const navGroups: NavGroup[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: FiZap,
    items: [
      { id: 'overview', label: 'Overview' },
      { id: 'authentication', label: 'Authentication' },
      { id: 'response-format', label: 'Response Format' },
    ]
  },
  {
    id: 'user-profile',
    title: 'User Profile',
    icon: FiUser,
    items: [
      { id: 'get-profile', label: '/auth/profile', method: 'GET', path: '/auth/profile', description: 'Get current user profile' },
      { id: 'update-profile', label: '/auth/profile', method: 'PUT', path: '/auth/profile', description: 'Update user profile' },
      { id: 'change-password', label: '/auth/profile/password', method: 'PUT', path: '/auth/profile/password', description: 'Change password' },
    ]
  },
  {
    id: 'api-key',
    title: 'API Key Management',
    icon: FiKey,
    items: [
      { id: 'get-api-key', label: '/auth/profile/api-key', method: 'GET', path: '/auth/profile/api-key', description: 'Get API key info', adminOnly: true },
      { id: 'generate-api-key', label: '/auth/profile/api-key', method: 'POST', path: '/auth/profile/api-key', description: 'Generate new API key', adminOnly: true },
      { id: 'copy-api-key', label: '/auth/profile/api-key/copy', method: 'POST', path: '/auth/profile/api-key/copy', description: 'Get full API key', adminOnly: true },
      { id: 'revoke-api-key', label: '/auth/profile/api-key', method: 'DELETE', path: '/auth/profile/api-key', description: 'Revoke API key', adminOnly: true },
    ]
  },
  {
    id: 'user-management',
    title: 'User Management',
    icon: FiUsers,
    items: [
      { id: 'get-users', label: '/auth/users', method: 'GET', path: '/auth/users', description: 'Get all users', adminOnly: true },
      { id: 'get-user', label: '/auth/users/:id', method: 'GET', path: '/auth/users/:id', description: 'Get user by ID', adminOnly: true },
      { id: 'create-user', label: '/auth/users', method: 'POST', path: '/auth/users', description: 'Create new user', adminOnly: true },
      { id: 'update-user', label: '/auth/users/:id', method: 'PUT', path: '/auth/users/:id', description: 'Update user', adminOnly: true },
      { id: 'delete-user', label: '/auth/users/:id', method: 'DELETE', path: '/auth/users/:id', description: 'Delete user', adminOnly: true },
    ]
  },
  {
    id: 'user-settings',
    title: 'User Settings',
    icon: FiSettings,
    items: [
      { id: 'get-settings', label: '/auth/settings', method: 'GET', path: '/auth/settings', description: 'Get user settings' },
      { id: 'update-settings', label: '/auth/settings', method: 'PUT', path: '/auth/settings', description: 'Update user settings' },
      { id: 'reset-settings', label: '/auth/settings', method: 'DELETE', path: '/auth/settings', description: 'Reset to defaults' },
    ]
  },
  {
    id: 'session-management',
    title: 'Session Management',
    icon: FiShield,
    items: [
      { id: 'check-session', label: '/auth/session', method: 'GET', path: '/auth/session', description: 'Check current session' },
      { id: 'refresh-session', label: '/auth/refresh', method: 'POST', path: '/auth/refresh', description: 'Refresh session' },
      { id: 'get-sessions', label: '/auth/sessions', method: 'GET', path: '/auth/sessions', description: 'Get all sessions' },
      { id: 'delete-session', label: '/auth/sessions/:id', method: 'DELETE', path: '/auth/sessions/:sessionId', description: 'Delete session' },
      { id: 'logout', label: '/auth/logout', method: 'POST', path: '/auth/logout', description: 'Logout current' },
      { id: 'logout-all', label: '/auth/logout-all', method: 'POST', path: '/auth/logout-all', description: 'Logout all' },
    ]
  },
  {
    id: 'platform-login',
    title: 'Platform Login',
    icon: FiLogIn,
    items: [
      { id: 'login-platform', label: '/:app_name/login', method: 'POST', path: '/:app_name/login', description: 'Login to platform (prolamudi, carousell)' },
    ]
  },
  {
    id: 'login-status',
    title: 'Login Status',
    icon: FiActivity,
    items: [
      { id: 'status-platform', label: '/:app_name/login/status', method: 'GET', path: '/:app_name/login/status', description: 'Check platform login status' },
    ]
  },
  {
    id: 'browser-status',
    title: 'Browser Status',
    icon: FiMonitor,
    items: [
      { id: 'browser-status-endpoint', label: '/browsers/status', method: 'GET', path: '/browsers/status', description: 'Check browser status' },
    ]
  },
  {
    id: 'automation',
    title: 'Property Automation',
    icon: FiPlay,
    items: [
      { id: 'automate-platform', label: '/:app_name/automate', method: 'POST', path: '/:app_name/automate', description: 'Run platform automation (prolamudi, carousell)' },
    ]
  },
  {
    id: 'property-deletion',
    title: 'Property Deletion',
    icon: FiTrash2,
    items: [
      { id: 'delete-platform', label: '/:app_name/delete', method: 'POST', path: '/:app_name/delete', description: 'Delete properties by date' },
    ]
  },
];

const ApiDocs: React.FC = () => {
  const { user } = useAuth();
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [apiKeyCopying, setApiKeyCopying] = useState(false);
  const [expandedNavGroups, setExpandedNavGroups] = useState<string[]>(['getting-started']);
  const [activeSection, setActiveSection] = useState<string>('overview');

  const baseUrl = window.location.origin;

  const handleCopyApiKey = async () => {
    if (!user?.hasApiKey) {
      toast.error('No API key found. Generate one in Settings first.');
      return;
    }

    setApiKeyCopying(true);
    try {
      const response = await LoginAuthUser.copyMyApiKey();
      if (response.success && response.data?.apiKey) {
        await navigator.clipboard.writeText(response.data.apiKey);
        setApiKeyCopied(true);
        toast.success('API key copied to clipboard!');
        setTimeout(() => setApiKeyCopied(false), 3000);
      } else {
        toast.error('Failed to get API key');
      }
    } catch (error) {
      toast.error('Failed to copy API key');
    } finally {
      setApiKeyCopying(false);
    }
  };

  const toggleNavGroup = (groupId: string) => {
    setExpandedNavGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleNavClick = (itemId: string, groupId: string) => {
    setActiveSection(itemId);
    // Auto-expand the group when clicking an item
    if (!expandedNavGroups.includes(groupId)) {
      setExpandedNavGroups(prev => [...prev, groupId]);
    }
  };

  const getMethodClass = (method?: string): string => {
    if (!method) return '';
    const classes: Record<string, string> = {
      GET: 'method-get',
      POST: 'method-post',
      PUT: 'method-put',
      DELETE: 'method-delete'
    };
    return classes[method] || '';
  };

  // Find current item info
  const findCurrentItem = (): { item: NavItem | null; group: NavGroup | null } => {
    for (const group of navGroups) {
      const item = group.items.find(i => i.id === activeSection);
      if (item) return { item, group };
    }
    return { item: null, group: null };
  };

  const { item: currentItem, group: currentGroup } = findCurrentItem();

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="content-section">
            <div className="content-header">
              <h1><FiZap /> API Overview</h1>
              <p>Complete guide to integrating with the CGR Automation API</p>
            </div>

            <div className="quick-start-grid">
              <div className="quick-start-card">
                <h3><FiServer /> Base URL</h3>
                <CodeBlock language="url" code={`${baseUrl}/api`} />
              </div>

              <div className="quick-start-card">
                <h3><FiKey /> Your API Key</h3>
                {user?.hasApiKey ? (
                  <div className="api-key-display-docs">
                    <code className="api-key-masked">{user.apiKey}</code>
                    <button 
                      className={`btn-copy-key ${apiKeyCopied ? 'copied' : ''}`}
                      onClick={handleCopyApiKey}
                      disabled={apiKeyCopying}
                    >
                      {apiKeyCopied ? <><FiCheck /> Copied</> : <><FiCopy /> Copy Key</>}
                    </button>
                  </div>
                ) : (
                  <div className="no-api-key-notice">
                    <FiAlertCircle />
                    <span>No API key. Go to <a href="/settings">Settings → Security</a> to generate one.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="info-card">
              <h3>Quick Links</h3>
              <div className="quick-links">
                <button onClick={() => setActiveSection('authentication')} className="quick-link-btn">
                  <FiKey /> Authentication
                </button>
                <button onClick={() => setActiveSection('response-format')} className="quick-link-btn">
                  <FiCode /> Response Format
                </button>
                <button onClick={() => setActiveSection('get-profile')} className="quick-link-btn">
                  <FiUser /> User Profile
                </button>
              </div>
            </div>

            <div className="info-card">
              <h3>Supported Platforms</h3>
              <p>The <code>:app_name</code> parameter accepts the following values:</p>
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><code>prolamudi</code> - Pro Lamudi platform</li>
                <li><code>carousell</code> - Carousell platform</li>
              </ul>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="content-section">
            <div className="content-header">
              <h1><FiKey /> Authentication</h1>
              <p>All API requests require authentication via API key</p>
            </div>

            <div className="info-card highlight">
              <h3>Request Header</h3>
              <CodeBlock language="http" code={`X-API-Key: cgr_your_api_key_here`} />
            </div>

            <h3>Example Requests</h3>
            <CodeBlock 
              language="bash" 
              title="cURL"
              code={`curl -X GET "${baseUrl}/api/auth/profile" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json"`}
            />

            <CodeBlock 
              language="javascript" 
              title="JavaScript (fetch)"
              code={`const response = await fetch('${baseUrl}/api/auth/profile', {
  method: 'GET',
  headers: {
    'X-API-Key': 'cgr_your_api_key_here',
    'Content-Type': 'application/json'
  }
});
const data = await response.json();`}
            />

            <CodeBlock 
              language="python" 
              title="Python (requests)"
              code={`import requests

response = requests.get(
    '${baseUrl}/api/auth/profile',
    headers={
        'X-API-Key': 'cgr_your_api_key_here',
        'Content-Type': 'application/json'
    }
)
data = response.json()`}
            />
          </div>
        );

      case 'response-format':
        return (
          <div className="content-section">
            <div className="content-header">
              <h1><FiCode /> Response Format</h1>
              <p>All responses follow a consistent JSON structure</p>
            </div>

            <div className="response-examples">
              <div className="response-example success">
                <h4><FiCheckCircle /> Success Response</h4>
                <CodeBlock language="json" code={`{
  "success": true,
  "data": { ... },
  "message": "Operation completed"
}`} />
              </div>

              <div className="response-example error">
                <h4><FiAlertCircle /> Error Response</h4>
                <CodeBlock language="json" code={`{
  "success": false,
  "message": "Error description",
  "error": "Details (optional)"
}`} />
              </div>
            </div>

            <h3>HTTP Status Codes</h3>
            <table className="status-codes-table">
              <thead>
                <tr><th>Code</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>200</code></td><td>Success</td></tr>
                <tr><td><code>201</code></td><td>Created</td></tr>
                <tr><td><code>400</code></td><td>Bad Request</td></tr>
                <tr><td><code>401</code></td><td>Unauthorized</td></tr>
                <tr><td><code>403</code></td><td>Forbidden</td></tr>
                <tr><td><code>404</code></td><td>Not Found</td></tr>
                <tr><td><code>500</code></td><td>Server Error</td></tr>
              </tbody>
            </table>
          </div>
        );

      // User Profile Endpoints
      case 'get-profile':
        return renderEndpointContent('GET', '/auth/profile', 'Get current user profile', {
          response: `{
  "success": true,
  "data": {
    "user": {
      "id": 1,
      "username": "admin",
      "email": "admin@example.com",
      "role": "admin",
      "apiKey": "cgr_d54f...3ff5",
      "hasApiKey": true
    }
  }
}`
        });

      case 'update-profile':
        return renderEndpointContent('PUT', '/auth/profile', 'Update user profile', {
          requestBody: `{ "email": "newemail@example.com" }`,
          response: `{
  "success": true,
  "data": { "id": 1, "email": "newemail@example.com" },
  "message": "Profile updated successfully"
}`
        });

      case 'change-password':
        return renderEndpointContent('PUT', '/auth/profile/password', 'Change password', {
          requestBody: `{
  "oldPassword": "current_password",
  "newPassword": "new_secure_password"
}`,
          response: `{ "success": true, "message": "Password updated successfully" }`
        });

      // API Key Management
      case 'get-api-key':
        return renderEndpointContent('GET', '/auth/profile/api-key', 'Get API key info (masked)', {
          adminOnly: true,
          response: `{
  "success": true,
  "data": { 
    "hasApiKey": true, 
    "apiKey": "cgr_d54f...3ff5", 
    "createdAt": "2025-12-02T10:35:19.000Z" 
  }
}`
        });

      case 'generate-api-key':
        return renderEndpointContent('POST', '/auth/profile/api-key', 'Generate new API key', {
          adminOnly: true,
          warning: 'Generating a new API key will invalidate the previous key.',
          response: `{
  "success": true,
  "data": { 
    "apiKey": "cgr_a1b2c3d4e5f6...", 
    "maskedApiKey": "cgr_a1b2...6789" 
  }
}`
        });

      case 'copy-api-key':
        return renderEndpointContent('POST', '/auth/profile/api-key/copy', 'Get full API key for copying', {
          adminOnly: true,
          response: `{ "success": true, "data": { "apiKey": "cgr_full_api_key_here..." } }`
        });

      case 'revoke-api-key':
        return renderEndpointContent('DELETE', '/auth/profile/api-key', 'Revoke API key', {
          adminOnly: true,
          danger: 'This action cannot be undone.',
          response: `{ "success": true, "message": "API key revoked successfully" }`
        });

      // User Management
      case 'get-users':
        return renderEndpointContent('GET', '/auth/users', 'Get all users', {
          adminOnly: true,
          response: `{
  "success": true,
  "data": [
    { "id": 1, "username": "admin", "email": "admin@example.com", "role": "admin" },
    { "id": 2, "username": "user1", "email": "user1@example.com", "role": "user" }
  ]
}`
        });

      case 'get-user':
        return renderEndpointContent('GET', '/auth/users/:id', 'Get user by ID', {
          adminOnly: true,
          params: [{ name: 'id', type: 'number', description: 'User ID' }],
          response: `{
  "success": true,
  "data": { "id": 1, "username": "admin", "email": "admin@example.com", "role": "admin" }
}`
        });

      case 'create-user':
        return renderEndpointContent('POST', '/auth/users', 'Create new user', {
          adminOnly: true,
          requestBody: `{
  "username": "newuser",
  "email": "newuser@example.com",
  "password": "secure_password",
  "role": "user"
}`,
          response: `{
  "success": true,
  "data": { "id": 3, "username": "newuser", "email": "newuser@example.com", "role": "user" },
  "message": "User created successfully"
}`
        });

      case 'update-user':
        return renderEndpointContent('PUT', '/auth/users/:id', 'Update user', {
          adminOnly: true,
          params: [{ name: 'id', type: 'number', description: 'User ID' }],
          requestBody: `{ "email": "updated@example.com", "role": "admin", "password": "new_password" }`,
          response: `{ "success": true, "message": "User updated successfully" }`
        });

      case 'delete-user':
        return renderEndpointContent('DELETE', '/auth/users/:id', 'Delete user', {
          adminOnly: true,
          params: [{ name: 'id', type: 'number', description: 'User ID' }],
          danger: 'This will permanently delete the user and all associated data.',
          response: `{ "success": true, "message": "User deleted successfully" }`
        });

      // User Settings
      case 'get-settings':
        return renderEndpointContent('GET', '/auth/settings', 'Get user settings', {
          response: `{
  "success": true,
  "data": {
    "autoLogout": true,
    "autoLogoutTime": 30,
    "twoFactorAuth": false,
    "sessionTimeout": 60,
    "rememberDevice": true
  }
}`
        });

      case 'update-settings':
        return renderEndpointContent('PUT', '/auth/settings', 'Update user settings', {
          requestBody: `{ "autoLogout": true, "autoLogoutTime": 60, "twoFactorAuth": true }`,
          response: `{ "success": true, "message": "Settings updated successfully" }`
        });

      case 'reset-settings':
        return renderEndpointContent('DELETE', '/auth/settings', 'Reset settings to default', {
          response: `{ "success": true, "message": "Settings reset to default" }`
        });

      // Session Management
      case 'check-session':
        return renderEndpointContent('GET', '/auth/session', 'Check current session', {
          response: `{
  "success": true,
  "data": { 
    "user": { "id": 1, "username": "admin" }, 
    "sessionId": "abc123...", 
    "csrfToken": "xyz789..." 
  }
}`
        });

      case 'refresh-session':
        return renderEndpointContent('POST', '/auth/refresh', 'Refresh session (extend expiry)', {
          response: `{ "success": true, "data": { "sessionExpiry": "2025-12-03T10:00:00.000Z" } }`
        });

      case 'get-sessions':
        return renderEndpointContent('GET', '/auth/sessions', 'Get all active sessions', {
          response: `{
  "success": true,
  "data": [
    { "id": "session_123", "user_agent": "Mozilla/5.0...", "ip_address": "192.168.1.1" }
  ]
}`
        });

      case 'delete-session':
        return renderEndpointContent('DELETE', '/auth/sessions/:sessionId', 'Terminate specific session', {
          params: [{ name: 'sessionId', type: 'string', description: 'Session ID' }],
          response: `{ "success": true, "message": "Session deleted" }`
        });

      case 'logout':
        return renderEndpointContent('POST', '/auth/logout', 'Logout current session', {
          response: `{ "success": true, "message": "Logged out successfully" }`
        });

      case 'logout-all':
        return renderEndpointContent('POST', '/auth/logout-all', 'Logout all sessions', {
          warning: 'This will terminate all active sessions including the current one.',
          response: `{ "success": true, "message": "All sessions terminated" }`
        });

      // Platform Login
      case 'login-platform':
        return renderEndpointContent('POST', '/:app_name/login', 'Login to platform', {
          params: [{ name: 'app_name', type: 'string', description: 'Platform name (prolamudi, carousell)' }],
          requestBody: `{ "email": "your-email@example.com", "password": "your-password" }`,
          response: `{ "success": true, "message": "Logged in to platform successfully" }`,
          curlExample: `# Example: Login to Pro Lamudi
curl -X POST "${baseUrl}/api/prolamudi/login" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password123"}'

# Example: Login to Carousell
curl -X POST "${baseUrl}/api/carousell/login" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password123"}'`
        });

      // Login Status
      case 'status-platform':
        return renderEndpointContent('GET', '/:app_name/login/status', 'Check platform login status', {
          params: [{ name: 'app_name', type: 'string', description: 'Platform name (prolamudi, carousell)' }],
          response: `{ "success": true, "data": { "statusCode": 200, "isLoggedIn": true } }`,
          curlExample: `# Example: Check Pro Lamudi status
curl -X GET "${baseUrl}/api/prolamudi/login/status" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json"

# Example: Check Carousell status
curl -X GET "${baseUrl}/api/carousell/login/status" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json"`
        });

      // Browser Status
      case 'browser-status-endpoint':
        return renderEndpointContent('GET', '/browsers/status', 'Check Playwright browser status', {
          response: `{ "success": true, "data": { "browserExists": true } }`
        });

      // Automation
      case 'automate-platform':
        return renderEndpointContent('POST', '/:app_name/automate', 'Run platform automation', {
          params: [
            { name: 'app_name', type: 'string', description: 'Platform name (prolamudi, carousell)' },
            { name: 'propertyId', type: 'string', description: 'Airtable record ID' }
          ],
          requestBody: `{ "propertyId": "rec123456789" }`,
          response: `{ "success": true, "message": "Automation started", "data": { "jobId": "job_123" } }`,
          curlExample: `# Example: Run Carousell automation
curl -X POST "${baseUrl}/api/carousell/automate" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"propertyId": "rec123456789"}'

# Example: Run Pro Lamudi automation
curl -X POST "${baseUrl}/api/prolamudi/automate" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"propertyId": "rec123456789"}'`
        });

      // Property Deletion
      case 'delete-platform':
        return renderEndpointContent('POST', '/:app_name/delete', 'Delete properties by date range', {
          params: [
            { name: 'app_name', type: 'string', description: 'Platform name (prolamudi, carousell)' },
            { name: 'startDate', type: 'string', description: 'Start date (YYYY-MM-DD)' },
            { name: 'endDate', type: 'string', description: 'End date (YYYY-MM-DD)' }
          ],
          requestBody: `{ "startDate": "2024-01-01", "endDate": "2024-01-31" }`,
          danger: 'This will delete all properties in the date range. Cannot be undone.',
          response: `{ "success": true, "message": "Properties deleted", "data": { "deletedCount": 15 } }`,
          curlExample: `# Example: Delete Pro Lamudi properties
curl -X POST "${baseUrl}/api/prolamudi/delete" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"startDate": "2024-01-01", "endDate": "2024-01-31"}'`
        });

      default:
        return (
          <div className="content-section">
            <div className="content-header">
              <h1>Select an endpoint</h1>
              <p>Choose an endpoint from the sidebar to view documentation</p>
            </div>
          </div>
        );
    }
  };

  // Helper function to render endpoint content
  const renderEndpointContent = (
    method: string, 
    path: string, 
    description: string, 
    options: {
      requestBody?: string;
      response?: string;
      params?: Array<{ name: string; type: string; description: string }>;
      warning?: string;
      danger?: string;
      adminOnly?: boolean;
      curlExample?: string;
    }
  ) => {
    const methodColors: Record<string, string> = {
      GET: 'method-get',
      POST: 'method-post',
      PUT: 'method-put',
      DELETE: 'method-delete'
    };

    return (
      <div className="content-section">
        <div className="content-header endpoint-header-detail">
          <div className="endpoint-title-row">
            <span className={`method-badge large ${methodColors[method]}`}>{method}</span>
            <code className="endpoint-path-large">{path}</code>
            {options.adminOnly && <span className="admin-badge">Admin Only</span>}
          </div>
          <p>{description}</p>
        </div>

        {options.warning && (
          <div className="warning-box">
            <FiAlertCircle />
            <span>{options.warning}</span>
          </div>
        )}

        {options.danger && (
          <div className="warning-box danger">
            <FiAlertCircle />
            <span>{options.danger}</span>
          </div>
        )}

        {options.params && options.params.length > 0 && (
          <>
            <h3>Parameters</h3>
            <table className="params-table">
              <thead>
                <tr><th>Name</th><th>Type</th><th>Description</th></tr>
              </thead>
              <tbody>
                {options.params.map((param, idx) => (
                  <tr key={idx}>
                    <td><code>{param.name}</code></td>
                    <td>{param.type}</td>
                    <td>{param.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {options.requestBody && (
          <>
            <h3>Request Body</h3>
            <CodeBlock language="json" code={options.requestBody} />
          </>
        )}

        <h3>Example Request</h3>
        <CodeBlock 
          language="bash" 
          title="cURL"
          code={options.curlExample || `curl -X ${method} "${baseUrl}/api${path}" \\
  -H "X-API-Key: cgr_your_api_key_here" \\
  -H "Content-Type: application/json"${options.requestBody ? ` \\
  -d '${options.requestBody.replace(/\n/g, '').replace(/\s+/g, ' ')}'` : ''}`}
        />

        {options.response && (
          <>
            <h3>Response</h3>
            <CodeBlock language="json" code={options.response} />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="api-docs-container">
      {/* Sidebar Navigation */}
      <nav className="api-docs-sidebar">
        <div className="sidebar-header">
          <h2><FiBook /> API Docs</h2>
          <span className="version-badge">v2.0</span>
        </div>

        {navGroups.map(group => (
          <div key={group.id} className="nav-group">
            <div 
              className={`nav-group-header ${expandedNavGroups.includes(group.id) ? 'expanded' : ''}`}
              onClick={() => toggleNavGroup(group.id)}
            >
              <span className="nav-group-title">
                <group.icon />
                {group.title}
              </span>
              {expandedNavGroups.includes(group.id) ? <FiChevronDown /> : <FiChevronRight />}
            </div>
            <div className={`nav-group-items ${expandedNavGroups.includes(group.id) ? 'expanded' : ''}`}>
              {group.items.map((item: NavItem) => (
                <div 
                  key={item.id} 
                  className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id, group.id)}
                >
                  {item.method && (
                    <span className={`method-badge-small ${getMethodClass(item.method)}`}>
                      {item.method}
                    </span>
                  )}
                  <span className="nav-item-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Main Content */}
      <main className="api-docs-main">
        {/* Breadcrumb */}
        {currentGroup && currentItem && (
          <div className="breadcrumb">
            <button onClick={() => setActiveSection('overview')} className="breadcrumb-link">
              <FiArrowLeft /> API Docs
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-group">{currentGroup.title}</span>
            {currentItem.method && (
              <>
                <span className="breadcrumb-separator">/</span>
                <span className={`method-badge-small ${getMethodClass(currentItem.method)}`}>
                  {currentItem.method}
                </span>
              </>
            )}
          </div>
        )}

        {renderContent()}
      </main>
    </div>
  );
};

export default ApiDocs;
